import { ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { routes } from '@/shared/constants'
import { formatFechaCorta, type NovedadDeProducto } from './novedades'

export interface NovedadCuerpoProps {
  novedad: NovedadDeProducto
  /**
   * Pinta el rótulo del producto en la línea de metadatos. En la página propia
   * va `false`: allí el rótulo ya está dentro de la portada ancha.
   */
  conProducto?: boolean
  /** Se llama al pulsar el enlace de detalles (el aviso lo usa para cerrarse). */
  onDetalles?: () => void
}

/**
 * El texto de una novedad: producto, versión, fecha, título y resumen, más el
 * enlace al histórico filtrado por ese producto.
 *
 * El cuerpo Markdown de la nota **no se pinta aquí**: para el detalle está
 * `/actualizaciones`, que ya existe y sabe renderizarlo.
 */
export function NovedadCuerpo({
  novedad,
  conProducto = true,
  onDetalles,
}: NovedadCuerpoProps) {
  const { t, i18n } = useTranslation()

  return (
    <>
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ink-subtle">
        {conProducto && (
          <span className="text-[11px] font-bold uppercase tracking-wider text-ink">
            {novedad.product}
          </span>
        )}
        {conProducto && <span aria-hidden="true">·</span>}
        <span className="tabular-nums">{novedad.version}</span>
        <span aria-hidden="true">·</span>
        <span>{formatFechaCorta(novedad.releasedAt, i18n.language)}</span>
      </div>

      <h3 className="mb-1 text-[15.5px] font-semibold leading-snug tracking-tight text-ink">
        {novedad.title}
      </h3>
      {/* `whitespace-pre-line`: el resumen trae una línea por cambio («• …»). */}
      <p className="m-0 whitespace-pre-line text-[13.5px] leading-relaxed text-ink-muted">
        {novedad.resumen}
      </p>

      {/* El filtro del histórico viaja por `repo` (slug estable), no por el
          rótulo: `product` es texto libre que el pipeline reescribe. */}
      <Link
        to={routes.public.releasesDeProducto(novedad.repo)}
        onClick={onDetalles}
        className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand hover:text-brand-dark"
      >
        {t('novedades.verDetalles')}
        <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
      </Link>
    </>
  )
}
